import React, { useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { FaTrash, FaPlus, FaSpinner, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import './MultiImageUpload.css';

const POSITION_OPTIONS = [
  { value: 'top', label: 'Atas Artikel' },
  { value: 'middle', label: 'Tengah Artikel' },
  { value: 'bottom', label: 'Bawah Artikel' }
];

const MultiImageUpload = ({ images = [], onChange, maxImages = 3, maxSizeKB = 100 }) => {
  const [uploading, setUploading] = useState(false);

  const compressImage = useCallback(async (file) => {
    const options = {
      maxSizeMB: maxSizeKB / 1024,
      maxWidthOrHeight: 1200,
      useWebWorker: true
    };

    try {
      const compressedFile = await imageCompression(file, options);
      console.log(`Compressed ${file.name}: ${(file.size / 1024).toFixed(1)}KB → ${(compressedFile.size / 1024).toFixed(1)}KB`);
      return compressedFile;
    } catch (error) {
      console.error('Compression error:', error);
      throw error;
    }
  }, [maxSizeKB]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      alert(`Maksimal ${maxImages} foto per postingan`);
      return;
    }

    const filesToProcess = files.slice(0, remainingSlots);
    setUploading(true);

    try {
      const newImages = await Promise.all(
        filesToProcess.map(async (file, index) => {
          // Compress image
          const compressedFile = await compressImage(file);
          
          // Convert to base64 for preview (will be uploaded to storage on form submit)
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(compressedFile);
          });

          return {
            id: `temp-${Date.now()}-${index}`,
            file: compressedFile,
            preview: base64,
            alt: file.name.replace(/\.[^/.]+$/, ''),
            position: 'top',
            isNew: true,
            originalSize: (file.size / 1024).toFixed(1),
            compressedSize: (compressedFile.size / 1024).toFixed(1)
          };
        })
      );

      onChange([...images, ...newImages]);
    } catch (error) {
      console.error('Error processing images:', error);
      alert('Gagal memproses gambar. Silakan coba lagi.');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  }, [images, maxImages, onChange, compressImage]);

  const handleRemove = (indexToRemove) => {
    onChange(images.filter((_, index) => index !== indexToRemove));
  };

  const handlePositionChange = (index, newPosition) => {
    const updatedImages = images.map((img, i) => 
      i === index ? { ...img, position: newPosition } : img
    );
    onChange(updatedImages);
  };

  const handleAltChange = (index, newAlt) => {
    const updatedImages = images.map((img, i) => 
      i === index ? { ...img, alt: newAlt } : img
    );
    onChange(updatedImages);
  };

  const moveImage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    
    const newImages = [...images];
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    onChange(newImages);
  };

  return (
    <div className="multi-image-upload">
      <div className="upload-header">
        <h4>Foto Artikel</h4>
        <span className="image-count">{images.length}/{maxImages} foto</span>
      </div>

      <p className="upload-hint">
        Maksimal {maxImages} foto, masing-masing akan dikompresi ke ~{maxSizeKB}KB. 
        Atur posisi foto di dalam artikel.
      </p>

      {/* Image Grid */}
      <div className="image-grid">
        {images.map((image, index) => (
          <div key={image.id || index} className="image-item">
            <div className="image-preview-wrapper">
              <img 
                src={image.preview || image.url} 
                alt={image.alt || `Foto ${index + 1}`}
                className="image-thumbnail"
              />
              <div className="image-overlay">
                <button 
                  type="button"
                  className="remove-btn"
                  onClick={() => handleRemove(index)}
                  title="Hapus foto"
                >
                  <FaTrash />
                </button>
              </div>
              {image.isNew && (
                <span className="size-badge">
                  {image.compressedSize}KB
                </span>
              )}
            </div>

            <div className="image-controls">
              <div className="reorder-buttons">
                <button
                  type="button"
                  onClick={() => moveImage(index, -1)}
                  disabled={index === 0}
                  title="Pindah ke atas"
                >
                  <FaArrowUp />
                </button>
                <span className="image-number">{index + 1}</span>
                <button
                  type="button"
                  onClick={() => moveImage(index, 1)}
                  disabled={index === images.length - 1}
                  title="Pindah ke bawah"
                >
                  <FaArrowDown />
                </button>
              </div>

              <select
                value={image.position}
                onChange={(e) => handlePositionChange(index, e.target.value)}
                className="position-select"
              >
                {POSITION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <input
                type="text"
                value={image.alt || ''}
                onChange={(e) => handleAltChange(index, e.target.value)}
                placeholder="Deskripsi foto"
                className="alt-input"
              />
            </div>
          </div>
        ))}

        {/* Add Button */}
        {images.length < maxImages && (
          <label className={`add-image-btn ${uploading ? 'uploading' : ''}`}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
              style={{ display: 'none' }}
            />
            {uploading ? (
              <>
                <FaSpinner className="spinner" />
                <span>Mengompresi...</span>
              </>
            ) : (
              <>
                <FaPlus />
                <span>Tambah Foto</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* Position Legend */}
      <div className="position-legend">
        <h5>Keterangan Posisi:</h5>
        <ul>
          <li><strong>Atas:</strong> Ditampilkan sebelum konten artikel</li>
          <li><strong>Tengah:</strong> Ditampilkan di tengah konten artikel</li>
          <li><strong>Bawah:</strong> Ditampilkan setelah konten artikel</li>
        </ul>
      </div>
    </div>
  );
};

export default MultiImageUpload;
